import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, BadgeCheck, Ban, CalendarPlus, CheckCircle2, Loader2, PauseCircle, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { AdminCard, Pill, Td, Th, TableWrap, inputCls } from "@/pages/admin/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS = {
  pending: { label: "En attente de paiement", tone: "amber" },
  active: { label: "Actif", tone: "green" },
  completed: { label: "Terminé", tone: "green" },
  suspended: { label: "Suspendu", tone: "slate" },
  expired: { label: "Expiré", tone: "rose" },
  revoked: { label: "Révoqué", tone: "rose" },
};

export default function AdminFormationStudents() {
  const [students, setStudents] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [formations, setFormations] = useState([]);
  const [q, setQ] = useState("");
  const [formationFilter, setFormationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, f] = await Promise.all([
        api.get("/admin/formation-students", { params: { q, formation_id: formationFilter, status: statusFilter } }),
        api.get("/admin/formation-certificates"),
        api.get("/admin/formations"),
      ]);
      setStudents(s.data); setCertificates(c.data); setFormations(f.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, formationFilter, statusFilter]);

  const act = async (row, action, days = 0) => {
    try {
      await api.put(`/admin/formation-access/${row.id}`, { action, days });
      toast.success("Accès mis à jour");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const [sendingCert, setSendingCert] = useState("");
  const sendCert = async (row) => {
    if (!window.confirm(`Envoyer le certificat « ${row.formation_title} » à ${row.customer_name || row.customer_email} ?`)) return;
    setSendingCert(row.id);
    try {
      const { data } = await api.post(`/admin/formation-access/${row.id}/certificate`);
      toast.success(data.created ? "Certificat envoyé — l'étudiant a été notifié 🎓" : "Certificat déjà délivré");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSendingCert(""); }
  };

  return (
    <div className="space-y-5">
      <Tabs defaultValue="students">
        <TabsList className="bg-transparent p-0">
          <TabsTrigger value="students" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Étudiants & accès</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="admin-tab-certificates" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Certificats</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="mt-5">
          <AdminCard title="Étudiants & gestion des accès" description="L'accès est créé automatiquement après confirmation du paiement. Vous pouvez l'ajuster manuellement.">
            <div className="mb-3 flex flex-wrap gap-2">
              <div className="relative max-w-xs flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client..." data-testid="students-search" />
              </div>
              <select className={`${inputCls} w-52`} value={formationFilter} onChange={(e) => setFormationFilter(e.target.value)}>
                <option value="">Toutes les formations</option>
                {formations.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
              </select>
              <select className={`${inputCls} w-44`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Tous les statuts</option>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
            ) : (
              <TableWrap>
                <table className="w-full text-left">
                  <thead><tr className="border-b border-line">
                    <Th>Client</Th><Th>Formation</Th><Th>Paiement</Th><Th>Progression</Th><Th>Statut</Th><Th>Accès</Th><Th className="text-right">Actions</Th>
                  </tr></thead>
                  <tbody>
                    {students.map((s) => {
                      const st = STATUS[s.status] || STATUS.active;
                      return (
                        <tr key={s.id} className="border-b border-line/70" data-testid={`student-row-${s.id}`}>
                          <Td>
                            <span className="font-bold text-ink">{s.customer_name || "—"}</span>
                            <span className="block text-xs text-slate-400">{s.customer_email}</span>
                          </Td>
                          <Td><span className="text-slate-600">{s.formation_title}</span></Td>
                          <Td><span className="text-xs text-slate-500">{s.order_number || "—"}</span></Td>
                          <Td>
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand" style={{ width: `${s.progress}%` }} /></div>
                              <span className="text-xs font-bold text-ink">{s.progress}%</span>
                            </div>
                          </Td>
                          <Td><Pill tone={st.tone}>{st.label}</Pill></Td>
                          <Td>
                            {s.has_certificate ? (
                              <span className="flex items-center gap-1 text-xs font-bold text-amber-600"><Award className="h-3.5 w-3.5" /> Certifié</span>
                            ) : (
                              <button onClick={() => sendCert(s)} disabled={!!sendingCert} data-testid={`send-cert-${s.id}`}
                                className="flex items-center gap-1 rounded-lg bg-amber-500 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-amber-600 disabled:opacity-60">
                                {sendingCert === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Award className="h-3 w-3" />} Envoyer
                              </button>
                            )}
                            <span className="block text-[11px] text-slate-400">{s.expires_at ? `Jusqu'au ${formatDate(s.expires_at)}` : "Permanent"}</span>
                          </Td>
                          <Td className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {s.status !== "active" && s.status !== "completed" && (
                                <button onClick={() => act(s, "grant")} title="Accorder / Activer" data-testid={`grant-${s.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><CheckCircle2 className="h-4 w-4" /></button>
                              )}
                              <button onClick={() => act(s, "extend", 30)} title="Prolonger (+30j)" className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-brand-soft hover:text-brand"><CalendarPlus className="h-4 w-4" /></button>
                              <button onClick={() => act(s, "suspend")} title="Suspendre" className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"><PauseCircle className="h-4 w-4" /></button>
                              <button onClick={() => act(s, "revoke")} title="Révoquer" data-testid={`revoke-${s.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Ban className="h-4 w-4" /></button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {students.length === 0 && <p className="py-12 text-center text-sm text-slate-500">Aucun étudiant pour l'instant.</p>}
              </TableWrap>
            )}
          </AdminCard>
        </TabsContent>

        <TabsContent value="certificates" className="mt-5">
          <AdminCard title="Certificats délivrés" description="Liste des certificats obtenus par les étudiants.">
            <TableWrap>
              <table className="w-full text-left">
                <thead><tr className="border-b border-line"><Th>N° Certificat</Th><Th>Étudiant</Th><Th>Formation</Th><Th>Délivré le</Th></tr></thead>
                <tbody>
                  {certificates.map((c) => (
                    <tr key={c.id} className="border-b border-line/70" data-testid={`cert-row-${c.id}`}>
                      <Td><span className="flex items-center gap-1.5 font-mono text-xs font-bold text-brand"><BadgeCheck className="h-4 w-4" /> {c.certificate_number}</span></Td>
                      <Td>{c.user_name || "—"}</Td>
                      <Td><span className="text-slate-600">{c.formation_title}</span></Td>
                      <Td>{formatDate(c.issued_at)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {certificates.length === 0 && <p className="py-12 text-center text-sm text-slate-500">Aucun certificat délivré.</p>}
            </TableWrap>
          </AdminCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
